import React, {useState} from 'react';

import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import CreatePartForm, {getCreateFormValidation, PartLinkFields} from 'molbio/parts/CreatePartForm';
import * as RegistryUtil from 'registry/RegistryUtil';
import {BioentityLinkFieldWithTargetSchema} from 'schemas/fields/SchemaFieldConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import {Optional} from 'util/Types';

export interface CreatePartModalPayload {
  name: string;
  folderId: string;
  partLinkField: BioentityLinkFieldWithTargetSchema | null;
  tagSchemaId?: string;
}

export interface CreatePartModalProps {
  initialFolderId?: string;
  namePlaceholder?: string;
  partLinkFields: PartLinkFields | null;
  onRequestClose: () => void;
  onRequestSubmit: (payload: CreatePartModalPayload) => void;
  displaySelectionStart: number;
  displaySelectionEnd: number;
}

export default function CreatePartModal(props: CreatePartModalProps): JSX.Element {
  const [name, setName] = useState<string>(props.namePlaceholder ?? '');
  const [folderId, setFolderId] = useState<Optional<string>>(
    props.initialFolderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY)
  );
  const [partLinkField, setPartLinkField] = useState<BioentityLinkFieldWithTargetSchema | null>(null);

  const {isValid} = getCreateFormValidation({name, folderId});

  const handleFolderIdChange = (newFolderId: string): void => {
    setFolderId(newFolderId);
    FolderDefaults.set(FolderDefaultsKey.INVENTORY, newFolderId);
  };

  const handleSubmit = (): void => {
    if (folderId) {
      const {folderId: parsedFolderId} = RegistryUtil.parseFolderValue(folderId);
      // TODO: Implement tagSchemaId
      props.onRequestSubmit({folderId: parsedFolderId!, name, tagSchemaId: undefined, partLinkField});
    }
    props.onRequestClose();
  };

  const description =
    'A new part will be created and this region ' +
    `(${props.displaySelectionStart}-${props.displaySelectionEnd}) ` +
    'will be replaced with it.';

  const buttons = [
    {
      text: 'Cancel',
      cancel: true,
    },
    {
      text: 'Replace',
      submit: true,
      style: ButtonStyle.PRIMARY,
      disabled: !isValid,
    },
  ];

  return (
    <Modal
      title="Replace selection with part"
      showHeaderBorder={false}
      enableRedesign={true}
      onRequestSubmit={handleSubmit}
      onRequestClose={props.onRequestClose}
      buttons={buttons}
    >
      <div className="u-small-padding-bottom">{description}</div>

      <CreatePartForm
        name={name}
        folderId={folderId}
        partLinkField={partLinkField}
        partLinkFields={props.partLinkFields}
        onNameChange={setName}
        onFolderIdChange={handleFolderIdChange}
        onPartLinkFieldChange={setPartLinkField}
      />
    </Modal>
  );
}
